import { useNavigation } from '@react-navigation/native';
import { Field, useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Avatar from '@components/Avatar/Avatar';
import { useBottomModal } from '@components/Modals/MobileModal';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import TextField from '@components/shared/TextField/TextField';
import { LocalFile } from '@graphql/generated';
import useChatAvatar from '@hooks/useChatAvatar';
import { MAX_GROUP_NAME_LENGTH } from '@schemas/createGroupSchema';
import { GroupChatSettingsValues } from '@screens/Chats/GroupChatSettingsProfileForm';
import { useFilePicker } from '@utils/filePicker';

// Only the fields this view reads. Callers pass either a full `Chat` or the
// narrower `GetChatDetail` query selection, so requiring `Chat` here breaks
// type-checking at the query-backed call sites.
export type GroupChatSettingsChat = {
  id: string;
  name: string;
  isGroupChat: boolean;
  avatarUrl?: string | null;
  avatar?: { id: string } | null;
};

interface GroupChatSettingsProfileProps {
  // `chat` can briefly be undefined: the modal stays mounted while the selected
  // chat is cleared or its query result is evicted/refetched.
  chat?: GroupChatSettingsChat;
  loading: boolean;
}

export const GroupChatSettingsProfileContent: React.FC<
  GroupChatSettingsProfileProps
> = ({ chat, loading }) => {
  const insets = useSafeAreaInsets();
  const inputHeight = 56 + insets.bottom;
  const navigation = useNavigation();
  const { getChatAvatarLabel } = useChatAvatar();
  const { launchImageSelection } = useFilePicker();
  const { values, isValid, submitForm, touched, setTouched, setFieldValue } =
    useFormikContext<GroupChatSettingsValues>();

  const { t } = useTranslation('models');
  const {
    avatar,
    name,
    isGroupChat,
    id: chatId,
  }: Partial<GroupChatSettingsChat> = chat ?? {};
  const { avatar: newAvatar } = values;

  const openCamera = async () => {
    navigation.navigate('expo-camera-photo-add-file', {
      onCallback: onPictureTaken,
      photoCount: 1,
    });
  };
  const { modal, showBottomModal } = useBottomModal();
  const onPictureTaken = async (r: LocalFile[]) => {
    r && setFieldValue('avatar', r[0]);
    setTouched({ ...touched, avatar: true });
  };

  const openGallery = async () => {
    await launchImageSelection(1).then((r) => {
      setTouched({ ...touched, avatar: true });
      r && setFieldValue('avatar', r[0]);
    });
  };

  const title = useMemo(() => {
    if (!chat) return '';
    return name && name !== '' ? name : getChatAvatarLabel();
  }, [chat, name, getChatAvatarLabel]);

  // Rendered after every hook call so hook order stays stable across renders.
  if (!chat) return null;

  return (
    <>
      {modal}
      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        <Box flex={1} marginTop='l' alignItems='center' marginHorizontal='m'>
          <Avatar
            avatarType='chat'
            id={chatId}
            avatar={newAvatar ?? undefined}
            avatarUrl={newAvatar ? undefined : chat.avatarUrl}
            size='large'
            isMultiple
            groupColorSeed={
              isGroupChat && typeof title === 'string' ? title : undefined
            }
            label={typeof title === 'string' ? title : ''}
            disableAvatarPress={true}
          />
          <TouchableOpacity
            onPress={() => {
              if (Platform.OS === 'web') openGallery();
              else if (Platform.OS === 'ios') {
                navigation.navigate('image-picker-modal', {
                  openCamera,
                  openGallery,
                });
              } else {
                showBottomModal({
                  items: [
                    {
                      title: t('shared:camera'),
                      iconName: 'Camera',
                      onSelect: openCamera,
                    },
                    {
                      title: t('shared:gallery'),
                      iconName: 'Image',
                      onSelect: openGallery,
                    },
                  ],
                });
              }
            }}>
            <Text variant='labelSmall' color='greenSecondary' marginTop='m'>
              {avatar ? 'Edit Photo' : 'Add Photo'}
            </Text>
          </TouchableOpacity>
          <Box width='100%' marginTop='l'>
            <Field
              component={TextField}
              name='name'
              label='Group Name'
              isLarge
              isRequired={true}
              showCharsRemaining
              textInputProps={{ maxLength: MAX_GROUP_NAME_LENGTH }}
            />
            <Field
              component={TextField}
              name='description'
              label='Group Description'
              placeholder='Enter description here...'
              isLarge
            />
          </Box>
        </Box>
      </ScrollView>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.select({
          ios: inputHeight,
          android: 500,
        })}>
        {Platform.OS === 'web' ? (
          <Box padding='m' flexDirection='row' justifyContent='flex-end'>
            <Button
              padding='l'
              variant='primary'
              onPress={submitForm}
              disabled={!isValid || Object.entries(touched).length < 1}>
              Save
            </Button>
          </Box>
        ) : (
          <Box marginBottom='l' marginHorizontal='m' marginTop='s'>
            <Button
              onPress={submitForm}
              variant='primary'
              disabled={
                !isValid ||
                Object.entries(touched).length < 1 ||
                loading ||
                !values.name
              }>
              Save
            </Button>
          </Box>
        )}
      </KeyboardAvoidingView>
    </>
  );
};
